import React, { useEffect, useRef, useState } from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CRow,
  CCol,
  CBadge,
  CFormTextarea,
  CImage,
  CAvatar,
  CModal,
} from "@coreui/react-pro";
import * as MarketsmsService from "../../services/MarketsmsService";
import { ConversationsItems } from "../../models/Conversations";
import '../../scss/_whatsapp_messages.scss';
import { ConversationMessagesItems } from "../../models/ConversationMessages";
import background from "../../assets/whatsapp/background.png";
import { db } from "../../firebase/FirebaseConfig";
import { getCurrentAccountId, getDefaultPhoneNumber, showToastTC } from "../../Utils/BaseApp";
import CIcon from "@coreui/icons-react";
import { BIcon } from "../../components/icons/BIcon";
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { cilSend } from "@coreui/icons";
import CustomAudioPlayer from "../../components/audio/CustomAudioPlayer";
import CustomVideo from "../../components/video/CustomVideo";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";

interface WhatsappConversationsProps { }

export const WhatsappConversations: React.FC<WhatsappConversationsProps> = () => {
  const [conversations, setConversations] = useState<ConversationsItems[] | null>(null);
  const [conversationsMessages, setConversationsMessages] = useState<ConversationMessagesItems[] | null>(null);
  const [selectConversations, setSelectConversations] = useState<ConversationsItems | null>(null);
  const [loadMessages, setLoadMessages] = useState(false);
  const [usedLabels, setUsedLabels] = useState(new Set());
  const datesArray: string[] = [];
  const navigate = useNavigate();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxRows = 5;
  
  // Referencia para mantener siempre actualizado el ID del chat seleccionado sin causar re-renderizados
  const selectConversationsRef = useRef<number | null>(null);

  // Mantener sincronizada la referencia cada vez que cambie de chat
  useEffect(() => {
    selectConversationsRef.current = selectConversations?.id ?? null;
  }, [selectConversations]);

  // 1. LISTENER DE LA LISTA DE CHATS (BARRA LATERAL)
  useEffect(() => {
    const local_phone_number_id = getDefaultPhoneNumber();
    if (local_phone_number_id != null) {
      const colRef = collection(db, 'chats');
      const q = query(colRef, where('phone_number_id', '==', local_phone_number_id));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        // Analizamos si el cambio en Firestore corresponde al chat que tenemos abierto actualmente
        let isCurrentChatUpdate = false;

        snapshot.docChanges().forEach((change) => {
          const docData = change.doc.data();

          console.log("------------docData------------");
          console.log(docData);
          console.log("------------selectConversationsRef------------");
          console.log(selectConversationsRef);
          console.log("-------conversations-------");
          console.log(conversations);
          console.log("-------conversationsMessages-------");
          console.log(conversationsMessages);
          // Comparamos si el chat actualizado es el mismo que está abierto
          if (selectConversationsRef.current && docData.conversation_id === selectConversationsRef.current) {
            isCurrentChatUpdate = true;
          }
        });

        // INTELIGENCIA: Si el mensaje nuevo pertenece al chat que ya tienes abierto,
        // la lista lateral NO necesita recargarse del servidor por ahora (evitamos llamada redundante),
        // ya que el chat abierto ya se está actualizando solo por su cuenta.
        if (isCurrentChatUpdate) {
          return;
        }

        // Si es para otro chat (un chat en segundo plano), sí actualizamos la barra lateral
        setTimeout(() => {
          getConversations(local_phone_number_id);
        }, 300);
      });

      return () => unsubscribe();
    }
  }, []); // Sin dependencias, vive durante toda la sesión usando la referencia mutable

  // 2. LISTENER DEL CHAT ACTIVO (MENSAJES INTERNOS)
  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (selectConversations) {
      selectConversations.unread = 0;

      // Carga inmediata al cambiar de chat
      getConversationMessages(selectConversations.id);
      initialLoadRef.current = true;

      const local_phone_number_id = getDefaultPhoneNumber();
      if (local_phone_number_id != null) {
        const colRef = collection(db, 'conversations');
        const q = query(colRef, where('conversation_id', '==', selectConversations.id));

        const unsubscribe = onSnapshot(q, () => {
          if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
          }

          // Mensaje nuevo real dentro del chat abierto
          getConversationMessages(selectConversations.id);
        });

        return () => {
          unsubscribe();
          initialLoadRef.current = false;
        };
      }
    }
  }, [selectConversations]);

  const getConversations = async (local_phone_number_id: number) => {
    const account_id = await getCurrentAccountId();
    MarketsmsService.getConversations(local_phone_number_id, account_id).then((response) => {
      if (response.status === 200) {
        setConversations(response.data.items);
      }
    });
  }

  const getConversationMessages = async (local_conversation_id: number) => {
    const account_id = await getCurrentAccountId();
    const phone_number_id = getDefaultPhoneNumber();

    if (phone_number_id != null) {
      MarketsmsService.getConversationMessages(phone_number_id, local_conversation_id, account_id).then((response) => {
        if (response.status === 200) {
          setConversationsMessages(response.data.items);
          // response.data.items[response.data.items.length - 1].body;

          setConversations(prevConversations => {
            if (!prevConversations) return null;

            return prevConversations.map(chat => {
              if (chat.id === selectConversationsRef.current) {
                return {
                  ...chat,
                  last_message: response.data.items[response.data.items.length - 1].body ??  "Archivo adjunto",
                  message_date: response.data.items[response.data.items.length - 1].created_at,
                  type: response.data.items[response.data.items.length - 1].type,
                  message_status: response.data.items[response.data.items.length - 1].whatsapp_statuses_id
                };
              }
              return chat;
            });
          });
        }
      });
    } else {
      showToastTC("No contiene un numero de telefono por defecto, seleccionalo por favor", 5000, "error");
      setTimeout(() => navigate("/whatsapp/numbers"), 5000);
    }
  }

  // --- LÓGICA DE MENSAJES Y UI ---
  const [message, setMessage] = useState<string>("");
  const cardBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = parseInt(window.getComputedStyle(textareaRef.current).lineHeight, 10);
      const maxHeight = lineHeight * maxRows;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [message]);

  useEffect(() => {
    if (cardBodyRef.current) {
      cardBodyRef.current.scrollTop = cardBodyRef.current.scrollHeight;
    }
  }, [conversationsMessages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey) {
        e.preventDefault();
        const cursorPosition = e.currentTarget.selectionStart ?? 0;
        setMessage(message.slice(0, cursorPosition) + '\n' + message.slice(cursorPosition));
      } else {
        e.preventDefault();
        sendIndividualMessage();
      }
    }
  };
  const sendIndividualMessage = async () => {
    const sms = message.trim();
    if (sms.length > 0 || selectedFile != null || audioUrl != null) {

      const currentFile = selectedFile;
      const currentAudioUrl = audioUrl;
      const currentPreviewUrl = previewUrl;

      // Limpiamos los inputs al instante
      setMessage("");
      clearSelectedFile();
      setRecordingState('stop');
      setAudioUrl(null);

      let optimisticMediaUrl = "";
      if (currentFile && currentPreviewUrl) {
        let fakeExt = ".jpg";
        if (currentFile.type.startsWith('video/')) fakeExt = ".mp4";
        else if (currentFile.type.startsWith('audio/')) fakeExt = ".mp3";
        optimisticMediaUrl = `${currentPreviewUrl}#ext=${fakeExt}`;
      } else if (currentAudioUrl) {
        optimisticMediaUrl = `${currentAudioUrl}#ext=.mp3`;
      }

      if (selectConversations?.id) {
        if (conversationsMessages) {
          // Inyectamos un ÚNICO objeto que contiene tanto la imagen como el texto (body)
          setConversationsMessages([...conversationsMessages, {
            id: Date.now(),
            type: "outbound",
            body: sms, // El texto viaja junto con el archivo
            variables: "string",
            media_url: optimisticMediaUrl,
            whatsapp_templates_id: 0,
            service_types_id: 222,
            whatsapp_statuses_id: 1,
            campaigns_id: null,
            template: null,
            created_at: getCurrentDateTime(),
            updated_at: getCurrentDateTime()
          }]);
        }

        const account_id = await getCurrentAccountId();
        const isVoice = currentFile?.type.startsWith('audio/') ?? false;

        MarketsmsService.sendIndividualMessage(selectConversations.id, account_id, sms, currentFile, isVoice).then(() => {
          const local_phone_number_id = getDefaultPhoneNumber();
          if (local_phone_number_id) getConversations(local_phone_number_id);
          getConversationMessages(selectConversations.id);
        });
      }
    }
  }

  const getCurrentDateTime = (): string => {
    const now = new Date();
    return format(now, 'yyyy-MM-dd HH:mm:ss');
  };

  // --- LÓGICA DE GRABACIÓN DE AUDIO ---
  const [recordingState, setRecordingState] = useState<string>("stop");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [time, setTime] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    if (recordingState === 'pause') {
      mediaRecorderRef.current?.resume();
      setRecordingState('start');
      startTimer();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const url = URL.createObjectURL(audioBlob);
          setAudioUrl(url);

          const audioFile = new File([audioBlob], "audio_message.webm", { type: 'audio/webm' });
          setSelectedFile(audioFile);

          audioChunksRef.current = [];
          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorderRef.current.start();
        setRecordingState('start');
        setTime(0);
        audioChunksRef.current = [];
        startTimer();
      } catch (err) {
        console.error('Error al acceder al micrófono.', err);
        showToastTC("No se pudo acceder al micrófono", 3000, "error");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecordingState('review');
    stopTimer();
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecordingState('stop');
    stopTimer();
    setAudioUrl(null);
    clearSelectedFile();
  }

  const startTimer = () => {
    intervalRef.current = setInterval(() => setTime((prevTime) => prevTime + 1), 1000);
  };

  const stopTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // --- MANEJO DE ARCHIVOS ADJUNTOS ---
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (recordingState !== 'stop') {
      setRecordingState('stop');
      setAudioUrl(null);
    }
  };

  const previewMedia = (file: File) => {
    const type = file.type;
    if (type.startsWith("image/")) return <img src={previewUrl} alt="preview" className="rounded" style={{ height: '60px', objectFit: 'cover' }} />;
    if (type.startsWith("video/")) return <video height={60} className="rounded" src={previewUrl}></video>;
    if (type.startsWith("audio/")) return <audio controls src={previewUrl} style={{ height: '40px' }}></audio>;
    return (
      <div className="d-flex align-items-center bg-light p-2 rounded border">
        <BIcon icon="file-earmark-text" size={5} className="me-2 text-primary" />
        <span className="fs-8 text-truncate" style={{ maxWidth: '150px' }}>{file.name}</span>
      </div>
    );
  };

  // --- UTILIDADES DE UI ---
  const formatText = (text: string) => {
    let formattedText = text;
    formattedText = formattedText.replace(/\*([^\*]+)\*/g, '<strong>$1</strong>');
    formattedText = formattedText.replace(/_([^_]+)_/g, '<em>$1</em>');
    formattedText = formattedText.replace(/~([^~]+)~/g, '<del>$1</del>');
    formattedText = formattedText.replace(/```([^`]+)```/g, '<code style="color:inherit">$1</code>');
    formattedText = formattedText.replace(/\n/g, '<br/>');
    return <div className="m-0 fs-8 mt-1 text-black" dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  function formatTimeTemplate(date: any) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const amOrPm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes < 10 ? '0' : ''}${minutes} ${amOrPm}`;
  }

  const lightColors = ['#FFB6C1', '#FFDEAD', '#FFE4B5', '#F5DEB3', '#F5F5DC', '#F0E68C', '#E6E6FA', '#FFFACD'];
  const getColorForIndex = (index: number) => lightColors[index % lightColors.length];
  const [visibleMedia, setVisibleMedia] = useState(false);
  const [srcMedia, setSrcMedia] = useState("");

  const showMedia = (media: string) => {
    setSrcMedia(media);
    setVisibleMedia(true);
  }

  // const getMediaType = (url: string) => {
  //   const ext = url.split('.').pop()?.toLowerCase();
  //   if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext!)) return 'image';
  //   if (['mp4', 'webm'].includes(ext!)) return 'video';
  //   if (['mp3', 'wav', 'ogg', 'opus'].includes(ext!)) return 'audio';
  //   return 'other';
  // };

  const getMediaType = (url: string) => {
    if (!url) return 'other';
    let ext = url.split('.').pop()?.toLowerCase();

    // Manejar el caso de nuestras URLs locales (optimistas)
    if (url.startsWith('blob:')) {
      const hashExt = url.split('#ext=')[1];
      if (hashExt) ext = hashExt.replace('.', '');
    }

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext!)) return 'image';
    if (['mp4', 'webm'].includes(ext!)) return 'video';
    if (['mp3', 'wav', 'ogg', 'opus'].includes(ext!)) return 'audio';
    return 'other';
  };

  const canSend = message.trim().length > 0 || selectedFile !== null || recordingState === 'review';

  return (
    <>
      <CCard className="vh-84 mb-1 border-0 shadow-sm">
        <CCardHeader className="bg-white border-bottom py-3">
          <strong className="fs-5">Conversaciones</strong>
        </CCardHeader>
        <CCardBody className="d-flex p-0 h-75">

          {/* BARRA LATERAL DE CHATS */}
          <CCol md={3} className="custom-overflow-y bg-white border-end">
            <CRow className="m-0 h-100">
              <CCol md={12} className="p-0">
                {conversations?.map((conversation, index) => (
                  <div key={index} onClick={() => setSelectConversations(conversation)}
                    className={`mx-2 p-2 rounded my-1 cursor-pointer transition-all ${selectConversations?.id === conversation.id ? 'bg-light' : 'hover-bg-light'}`}>
                    <CRow className="align-items-center">
                      <CCol xs={2}>
                        <CAvatar textColor="black" className="mx-auto fw-bold" style={{ background: getColorForIndex(index) }} size="md">
                          {(conversation.first_name ?? conversation.number).toString().substring(0, 2).toUpperCase()}
                        </CAvatar>
                      </CCol>
                      <CCol xs={10}>
                        <div className="d-flex justify-content-between align-items-center">
                          <p className="p-0 m-0 fw-semibold text-truncate" style={{ maxWidth: '70%' }}>{conversation.first_name ?? conversation.number}</p>
                          <p className="text-muted m-0 p-0 fs-9">{conversation.message_date ? conversation.message_date.substring(10, 16) : ""}</p>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <div className="d-flex align-items-center text-truncate" style={{ maxWidth: '85%' }}>
                            {conversation.type === 'outbound' && (
                              <BIcon icon={`check2${conversation.message_status === 4 || conversation.message_status === 6 ? '-all' : ''}`}
                                className={`me-1 fs-8 text-${conversation.message_status === 6 ? 'info' : 'secondary'}`} />
                            )}
                            {conversation.media_url && <BIcon icon="camera-fill" className="me-1 fs-8 text-secondary" />}
                            <p className="text-muted fs-8 m-0 text-truncate">
                              {conversation.last_message ? conversation.last_message : "Archivo adjunto"}
                            </p>
                          </div>
                          {conversation.unread > 0 && conversation.type === 'inbound' && (
                            <CBadge color="success" shape="rounded-pill" className="fs-9">{conversation.unread}</CBadge>
                          )}
                        </div>
                      </CCol>
                    </CRow>
                  </div>
                ))}
              </CCol>
            </CRow>
          </CCol>

          {/* ÁREA DEL CHAT */}
          {selectConversations ? (
            <CCard className="w-100 border-0 d-flex flex-column rounded-0 bg-transparent">
              {/* HEADER DEL CHAT */}
              <CCardHeader className="py-2 bg-white border-bottom d-flex align-items-center">
                <CAvatar textColor="black" style={{ background: getColorForIndex(conversations?.findIndex(c => c.id === selectConversations.id) || 0) }} size="md" className="me-3 fw-bold">
                  {(selectConversations.first_name ?? selectConversations.number).toString().substring(0, 2).toUpperCase()}
                </CAvatar>
                <div>
                  <p className="text-dark m-0 fs-6 fw-semibold">{selectConversations.first_name ?? selectConversations.number}</p>
                  <p className="text-muted m-0 fs-8">{selectConversations.number}</p>
                </div>
              </CCardHeader>

              {/* CUERPO DEL CHAT (MENSAJES) */}
              <CCardBody ref={cardBodyRef} className="d-flex flex-column overflow-auto p-4"
                style={{ backgroundImage: `url(${background})`, backgroundSize: 'contain', backgroundColor: '#efeae2' }}>

                {conversationsMessages?.map((message, index) => {
                  const createdAtDate = parseISO(message.created_at);
                  let fechaRelacionada = '';

                  if (isToday(createdAtDate)) {
                    fechaRelacionada = 'Hoy';
                  } else if (isYesterday(createdAtDate)) {
                    fechaRelacionada = 'Ayer';
                  } else {
                    fechaRelacionada = format(createdAtDate, 'dd/MM/yyyy');
                  }

                  if (datesArray.includes(fechaRelacionada)) {
                    fechaRelacionada = '';
                  } else {
                    datesArray.push(fechaRelacionada);
                  }

                  return (
                    <div key={index}>
                      <div className="text-center">
                        {!usedLabels.has(fechaRelacionada) && fechaRelacionada !== '' && (
                          <div className="text-center">
                            <CBadge className="bg-light text-dark my-3 px-3 py-2 shadow-sm rounded-pill">{fechaRelacionada}</CBadge>
                          </div>
                        )}
                      </div>
                      <div key={index} className={`m-1 chat-message ${message.type === 'outbound' ? 'outbound' : 'inbound'}`}>
                        {message.whatsapp_templates_id ?
                          <CCard className="text-center border-0 shadow-sm" style={{ width: "18rem" }}>
                            <CCardBody className="py-0 px-0">
                              <div className="d-flex flex-column">
                                <div className="bg-white rounded-end-3 rounded-bottom-3 pb-2 text-start" style={{ maxWidth: "100%", minWidth: "25%", position: 'relative', minHeight: "42px" }}>
                                  <div className="m-1 p-1">
                                    {message.template?.header_format == 'TEXT' && <p className="m-0 fs-7 fw-bolder">{message.template?.header_content}</p>}
                                    {message.template?.header_format != 'TEXT' && (
                                      <>
                                        {message.template?.header_format == 'IMAGE' ? (
                                          <CImage src={message.template?.header_content} className="rounded-2 w-100 h-50" />
                                        ) : message.template?.header_format == 'VIDEO' ? (
                                          <CustomVideo src={message.template?.header_content} className="rounded-2 w-100" onClick={() => { showMedia(message.template?.header_content ?? "") }} />
                                        ) : (
                                          <p>Preview no disponible</p>
                                        )}
                                      </>
                                    )}
                                    <div className="m-0 fs-8 mt-2 text-black">{formatText(message.body ?? "")}</div>
                                    <p className="m-0 fs-9 mt-2 text-gray">{message.template?.footer_content}</p>
                                    <p className="p-0 fs-9 text-end m-0 text-gray" style={{ bottom: '2px', right: '10px' }}>{formatTimeTemplate(new Date())}</p>
                                  </div>

                                  {(message.template?.buttons != undefined && message.template?.buttons.length > 0) && <hr className="my-2" />}
                                  <div className={`px-${(message.template?.buttons != undefined && message.template?.buttons.length > 0) ? 2 : 0}`}>
                                    {(message.template?.buttons != undefined && message.template?.buttons.length < 4) &&
                                      message.template?.buttons.map((button, index) => (
                                        <div key={index}>
                                          {index > 0 && <hr className="my-2" />}
                                          <p className="text-center text-info my-0 cursor-pointer hover-text-primary">
                                            <BIcon icon={button.type === 1 ? 'reply-fill' : button.type === 4 ? 'copy' : button.type === 2 ? 'box-arrow-up-right' : 'telephone-fill'} size={7} className="me-2" />
                                            {button.text}
                                          </p>
                                        </div>
                                      ))
                                    }
                                    {(message.template?.buttons != undefined && message.template?.buttons.length > 3) &&
                                      message.template?.buttons.slice(0, 2).map((button, index) => (
                                        <div key={index}>
                                          {index > 0 && <hr className="my-2" />}
                                          <p className="text-center text-info my-0 cursor-pointer">
                                            <BIcon icon={button.type === 1 ? 'reply-fill' : button.type === 4 ? 'copy' : button.type === 2 ? 'box-arrow-up-right' : 'telephone-fill'} size={7} className="me-2" />
                                            {button.text}
                                          </p>
                                          {index === 1 && (
                                            <div>
                                              <hr className="my-2" />
                                              <p className="text-center text-info my-0 cursor-pointer">
                                                <BIcon icon="list-ul" size={5} className="me-2" />Ver todas las opciones
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              </div>
                            </CCardBody>
                          </CCard>
                          :
                          <>
                            {message.media_url != "" && message.media_url != null && message.media_url.includes('.opus') ?
                              <div className="p-1 bg-light rounded shadow-sm">
                                <CustomAudioPlayer src={'https://api.marketsms.co/' + message.media_url} />
                                <p className=" fs-9 text-gray mb-0 text-end" style={{ minWidth: "2rem", marginTop: "-0.7rem !important" }}>{message.created_at.substring(10, 16)}</p>
                              </div>
                              :
                              <>
                                {message.media_url && message.body === '' ? (
                                  <div className="p-1 bg-light rounded shadow-sm" style={{ maxWidth: "256px" }}>
                                    {(() => {
                                      const mediaType = getMediaType(message.media_url!);
                                      const isBlob = message.media_url!.startsWith('blob:');
                                      const mediaSrc = isBlob ? message.media_url!.split('#ext=')[0] : 'https://api.marketsms.co/' + message.media_url;

                                      switch (mediaType) {
                                        case 'image':
                                          return <CImage width={256} src={mediaSrc} className="rounded cursor-pointer" onClick={() => showMedia(message.media_url!)} />;
                                        case 'video':
                                          return <video width={256} controls className="rounded"><source src={mediaSrc} type="video/mp4" /></video>;
                                        case 'audio':
                                          return <audio controls className="mt-1"><source src={mediaSrc} type="audio/mpeg" /></audio>;
                                        default:
                                          return <p className="m-2">Archivo adjunto</p>;
                                      }
                                    })()}
                                    {/* El texto se renderiza aquí mismo, dentro de la misma caja */}
                                    <div className="px-2 pt-1">{formatText(message.body ?? "")}</div>
                                    <div className="d-flex justify-content-end align-items-center mt-1 px-1">
                                      <p className="fs-9 text-gray mb-0 me-1">{message.created_at.substring(10, 16)}</p>
                                      {message.type === 'outbound' && (
                                        <BIcon icon={`check2${message.whatsapp_statuses_id === 4 || message.whatsapp_statuses_id === 6 ? '-all' : ''}`} className={`fs-9 text-${message.whatsapp_statuses_id === 6 ? 'info' : 'secondary'}`} />
                                      )}
                                    </div>
                                  </div>
                                ) : message.media_url && message.body !== '' ? (
                                  <div className="p-1 bg-light rounded shadow-sm" style={{ maxWidth: "500px" }}>
                                    {/* {(() => {
                                      const mediaType = getMediaType(message.media_url!);
                                      const mediaSrc = 'https://api.marketsms.co/' + message.media_url;

                                      switch (mediaType) {
                                        case 'image':
                                          return (
                                            <CImage width={256} src={mediaSrc} className="rounded cursor-pointer" title="Previsualizar imagen" onClick={() => showMedia(message.media_url!)} />
                                          );
                                        case 'video':
                                          return (
                                            <video width={256} controls className="rounded">
                                              <source src={mediaSrc} type="video/mp4" />
                                            </video>
                                          );
                                        case 'audio':
                                          return (
                                            <audio controls className="mt-1">
                                              <source src={mediaSrc} type="audio/mpeg" />
                                            </audio>
                                          );
                                        default:
                                          return <p className="m-2">Archivo adjunto</p>;
                                      }
                                    })()} */}
                                      {(() => {
                                        const mediaType = getMediaType(message.media_url!);
                                        const isBlob = message.media_url!.startsWith('blob:');
                                        const mediaSrc = isBlob ? message.media_url!.split('#ext=')[0] : 'https://api.marketsms.co/' + message.media_url;

                                        switch (mediaType) {
                                          case 'image':
                                            return <CImage width={256} src={mediaSrc} className="rounded cursor-pointer" onClick={() => showMedia(message.media_url!)} />;
                                          case 'video':
                                            return <video width={256} controls className="rounded"><source src={mediaSrc} type="video/mp4" /></video>;
                                          case 'audio':
                                            return <audio controls className="mt-1"><source src={mediaSrc} type="audio/mpeg" /></audio>;
                                          default:
                                            return <p className="m-2">Archivo adjunto</p>;
                                        }
                                      })()}
                                    <div className="px-1">{formatText(message.body ?? "")}</div>
                                    <div className="d-flex justify-content-end align-items-center mt-1 px-1">
                                      <p className="fs-9 text-gray mb-0 me-1">{message.created_at.substring(10, 16)}</p>
                                      {message.type === 'outbound' && (
                                        <BIcon icon={`check2${message.whatsapp_statuses_id === 4 || message.whatsapp_statuses_id === 6 ? '-all' : ''}`} className={`fs-9 text-${message.whatsapp_statuses_id === 6 ? 'info' : 'secondary'}`} />
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  // <div className="message-text m-0 p-2 d-flex pb-1 flex-column shadow-sm" style={{ minWidth: '10%' }}>
                                  //   {formatText(message.body ?? "")}
                                  //   <div className="text-end d-flex justify-content-end align-items-center mt-1 ms-3">
                                  //     <p className="fs-9 text-gray mb-0 me-1">{message.created_at.substring(10, 16)}</p>
                                  //     {message.type === 'outbound' && (
                                  //       <BIcon icon={`check2${message.whatsapp_statuses_id === 4 || message.whatsapp_statuses_id === 6 ? '-all' : ''}`} className={`fs-9 text-${message.whatsapp_statuses_id === 6 ? 'info' : 'secondary'}`} />
                                  //     )}
                                  //   </div>
                                      // </div>
                                      <div className="message-text m-0 p-2 d-flex pb-1 flex-column shadow-sm position-relative" style={{ minWidth: '10%' }}>
                                        {formatText(message.body ?? "")}

                                        <div className="text-end d-flex justify-content-end align-items-center mt-1 ms-3">
                                          <p className="fs-9 text-gray mb-0 me-1">{message.created_at.substring(10, 16)}</p>
                                          {message.type === 'outbound' && (
                                            <BIcon icon={`check2${message.whatsapp_statuses_id === 4 || message.whatsapp_statuses_id === 6 ? '-all' : ''}`} className={`fs-9 text-${message.whatsapp_statuses_id === 6 ? 'info' : 'secondary'}`} />
                                          )}
                                        </div>

                                        {/* Renderizado de la reacción flotando */}
                                        {message.reactions && message.reactions.length > 0 && (
                                          <div
                                            className="position-absolute bg-white border shadow-sm rounded-pill px-2 py-0 d-flex align-items-center"
                                            style={{ bottom: '-10px', left: '10px', zIndex: 2, fontSize: '0.95rem' }}
                                          >
                                            {message.reactions.map((reaction, rIndex) => (
                                              <span key={rIndex}>{reaction.body}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                )}
                              </>
                            }
                          </>
                        }
                      </div>
                    </div>
                  )
                })}
              </CCardBody>

              {/* ZONA DE INPUT (ESTILO WHATSAPP WEB) */}
              {/* <div className="bg-white p-3 d-flex align-items-end border-top shadow-sm">

        
                <div className="pb-1 pe-3">
                  <label htmlFor="fileInput" className="cursor-pointer">
                    <BIcon icon="plus-lg" size={5} className="text-secondary hover-text-dark transition-all" />
                  </label>
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>

          
                <div className="flex-grow-1 bg-light rounded-3 px-3 py-2 me-3 d-flex flex-column justify-content-center" style={{ minHeight: '44px' }}>

                  {recordingState === 'stop' && !selectedFile && (
                    <CFormTextarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      className="border-0 bg-transparent p-0 fs-7 focus-ring-none shadow-none"
                      style={{ resize: 'none' }}
                      placeholder="Escribe un mensaje"
                    />
                  )}

                  {selectedFile && recordingState === 'stop' && (
                    <div className="d-flex align-items-center justify-content-between">
                      {previewMedia(selectedFile)}
                      <BIcon icon="x-circle-fill" className="text-secondary cursor-pointer ms-2 fs-5 hover-text-danger" onClick={clearSelectedFile} />
                    </div>
                  )}

                  {(recordingState === 'start' || recordingState === 'review') && (
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center text-danger">
                        {recordingState === 'start' && <span className="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true"></span>}
                        <span className="fs-6 fw-semibold font-monospace">{formatTime(time)}</span>
                      </div>

                      {recordingState === 'review' && audioUrl ? (
                        <audio src={audioUrl} controls className="mx-3" style={{ height: '30px', flexGrow: 1 }} />
                      ) : (
                        <div className="text-muted fs-7 ms-3 flex-grow-1 text-center">Grabando audio...</div>
                      )}

                      <BIcon onClick={cancelRecording} icon="trash" size={5} className="text-secondary cursor-pointer hover-text-danger ms-3" />

                      {recordingState === 'start' && (
                        <BIcon onClick={stopRecording} icon="stop-circle" size={5} className="text-danger cursor-pointer ms-3" />
                      )}
                    </div>
                  )}
                </div>

           
                <div className="pb-1 d-flex align-items-center justify-content-center" style={{ width: '44px', height: '44px' }}>
                  {canSend ? (
                    <div className="bg-success rounded-circle d-flex align-items-center justify-content-center cursor-pointer shadow-sm"
                      style={{ width: '40px', height: '40px' }} onClick={sendIndividualMessage}>
                      <CIcon icon={cilSend} className="text-white ms-1" size="lg" />
                    </div>
                  ) : (
                    <div className="bg-success rounded-circle d-flex align-items-center justify-content-center cursor-pointer shadow-sm"
                      style={{ width: '40px', height: '40px' }} onClick={startRecording}>
                      <BIcon icon="mic-fill" className="text-white fs-5" />
                    </div>
                  )}
                </div>

              </div> */}

              {/* ZONA DE INPUT (ESTILO WHATSAPP WEB) */}
              <div className="bg-white p-2 d-flex align-items-end border-top shadow-sm">

                {/* Botón Adjuntar */}
                <div className="pb-2 pe-3 ps-2">
                  <label htmlFor="fileInput" className="cursor-pointer">
                    <BIcon icon="plus-lg" size={5} className="text-secondary hover-text-dark transition-all" />
                  </label>
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>

                {/* Área central (Input, Preview o Grabadora) */}
                <div className="flex-grow-1 bg-light rounded-3 px-3 py-2 me-3 d-flex flex-column justify-content-center">

                  {recordingState === 'stop' && (
                    <>
                      {/* Previsualización del archivo adjunto */}
                      {selectedFile && (
                        <div className="position-relative d-inline-block mb-2 mt-1" style={{ width: 'fit-content' }}>
                          <div className="bg-white p-1 rounded shadow-sm border d-flex align-items-center justify-content-center">
                            {previewMedia(selectedFile)}
                          </div>
                          {/* Botón X superpuesto en la esquina superior derecha de la imagen */}
                          <BIcon
                            icon="x-circle-fill"
                            className="text-secondary cursor-pointer position-absolute bg-white rounded-circle hover-text-danger shadow-sm"
                            style={{ top: '-10px', right: '-10px', fontSize: '1.25rem', zIndex: 10 }}
                            onClick={clearSelectedFile}
                          />
                        </div>
                      )}

                      {/* Input de texto (Garantizado que siempre sea visible) */}
                      <CFormTextarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        className="border-0 bg-transparent p-0 fs-7 focus-ring-none shadow-none"
                        style={{ resize: 'none', minHeight: '24px', outline: 'none' }}
                        placeholder={selectedFile ? "Añade un comentario..." : "Escribe un mensaje"}
                      />
                    </>
                  )}

                  {/* Vista durante la grabación / revisión de audio */}
                  {(recordingState === 'start' || recordingState === 'review') && (
                    <div className="d-flex align-items-center justify-content-between w-100 py-1">
                      <div className="d-flex align-items-center text-danger">
                        {recordingState === 'start' && <span className="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true"></span>}
                        <span className="fs-6 fw-semibold font-monospace">{formatTime(time)}</span>
                      </div>

                      {recordingState === 'review' && audioUrl ? (
                        <audio src={audioUrl} controls className="mx-3" style={{ height: '30px', flexGrow: 1 }} />
                      ) : (
                        <div className="text-muted fs-7 ms-3 flex-grow-1 text-center">Grabando audio...</div>
                      )}

                      <BIcon onClick={cancelRecording} icon="trash" size={5} className="text-secondary cursor-pointer hover-text-danger ms-3"  />

                      {recordingState === 'start' && (
                        <BIcon onClick={stopRecording} icon="stop-circle" size={5} className="text-danger cursor-pointer ms-3" />
                      )}
                    </div>
                  )}
                </div>

                {/* Botón de Enviar o Grabar */}
                <div className="pb-1 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                  {canSend ? (
                    <div className="bg-success rounded-circle d-flex align-items-center justify-content-center cursor-pointer shadow-sm"
                      style={{ width: '40px', height: '40px' }} onClick={sendIndividualMessage}>
                      <CIcon icon={cilSend} className="text-white ms-1" size="lg" />
                    </div>
                  ) : (
                    <div className="bg-success rounded-circle d-flex align-items-center justify-content-center cursor-pointer shadow-sm"
                      style={{ width: '40px', height: '40px' }} onClick={startRecording}>
                      <BIcon icon="mic-fill" className="text-white fs-5" />
                    </div>
                  )}
                </div>

              </div>
              
            </CCard>
          ) : (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center bg-light text-muted h-100">
              <BIcon icon="whatsapp" size={1} style={{ fontSize: '4rem', opacity: 0.2 }} className="mb-3" />
              <h5 className="fw-normal">Selecciona un chat para empezar</h5>
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* <CModal visible={visibleMedia} size="lg" onClose={() => setVisibleMedia(false)}>
        {srcMedia.match(/\.(jpeg|jpg|gif|png)$/i) ? (
          <CImage src={'https://api.marketsms.co/' + srcMedia} className="w-100 h-auto" />
        ) : (
          <CustomVideo src={srcMedia} controls={true} className="w-100" />
        )}
      </CModal> */}
      <CModal visible={visibleMedia} size="lg" onClose={() => setVisibleMedia(false)}>
        {(() => {
          const isBlobModal = srcMedia.startsWith('blob:');
          const finalSrc = isBlobModal ? srcMedia.split('#ext=')[0] : 'https://api.marketsms.co/' + srcMedia;

          if (srcMedia.match(/\.(jpeg|jpg|gif|png)$/i) || srcMedia.includes('#ext=.jpg')) {
            return <CImage src={finalSrc} className="w-100 h-auto" />;
          } else {
            return <CustomVideo src={finalSrc} controls={true} className="w-100" />;
          }
        })()}
      </CModal>
    </>
  );
};